import React, { useState, useEffect, useCallback } from 'react';
import { fetchStats } from '../api/stats';
import { fetchReviews } from '../api/study';
import { StatsResponse } from '../types/stats';
import { ReviewLog } from '../types/study';
import { ActivityChart } from '../components/ActivityChart';
import { useToast } from '../context/ToastContext';
import {
  Clock,
  RotateCw,
  TrendingUp,
  Flame,
} from 'lucide-react';

export const StatsPage: React.FC = () => {
  const { showToast } = useToast();

  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [reviews, setReviews] = useState<ReviewLog[]>([]);
  const [totalReviews, setTotalReviews] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsData, reviewsData] = await Promise.all([
        fetchStats(),
        fetchReviews(undefined, 50),
      ]);
      setStats(statsData);
      setReviews(reviewsData.items || []);
      setTotalReviews(reviewsData.total || 0);
    } catch (err: any) {
      showToast(err.message || '加载统计数据失败', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] text-slate-500">
        <div className="w-8 h-8 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin mb-3" />
        <p className="text-xs">加载学习统计与复习记录...</p>
      </div>
    );
  }

  // Calculate rating percentages
  const ratings = stats?.ratings || {};
  const againCount = ratings.again || 0;
  const hardCount = ratings.hard || 0;
  const normalCount = ratings.normal || 0;
  const easyCount = ratings.easy || 0;
  const totalRatings = againCount + hardCount + normalCount + easyCount || 1;

  const againPct = Math.round((againCount / totalRatings) * 100);
  const hardPct = Math.round((hardCount / totalRatings) * 100);
  const normalPct = Math.round((normalCount / totalRatings) * 100);
  const easyPct = Math.round((easyCount / totalRatings) * 100);

  const getRatingBadge = (rating: string) => {
    switch (rating) {
      case 'again':
        return <span className="px-2 py-0.5 rounded bg-red-100 text-red-700 font-semibold text-[10px]">重来 (Again)</span>;
      case 'hard':
        return <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold text-[10px]">困难 (Hard)</span>;
      case 'normal':
        return <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700 font-semibold text-[10px]">良好 (Good)</span>;
      case 'easy':
        return <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold text-[10px]">简单 (Easy)</span>;
      default:
        return <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px]">{rating}</span>;
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 tracking-tight">学习记录与记忆统计</h2>
          <p className="text-xs text-slate-500 mt-1">
            严谨记录真实复习历史，客观反映记忆衰减与巩固情况
          </p>
        </div>
        <button
          onClick={loadData}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 shadow-sm transition-colors"
        >
          <RotateCw className="w-3.5 h-3.5" />
          刷新数据
        </button>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">
            总词汇量
          </span>
          <div className="text-2xl font-extrabold text-slate-900">
            {stats?.words ?? 0}
          </div>
          <span className="text-[11px] text-slate-400 mt-1 block">收录单词与短语</span>
        </div>

        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">
            卡片总数
          </span>
          <div className="text-2xl font-extrabold text-slate-900">
            {stats?.cards ?? 0}
          </div>
          <span className="text-[11px] text-slate-400 mt-1 block">识别与拼写卡片</span>
        </div>

        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">
            今日已复习
          </span>
          <div className="text-2xl font-extrabold text-emerald-600">
            {stats?.reviews_today ?? 0}
          </div>
          <span className="text-[11px] text-slate-400 mt-1 block">次打卡交互</span>
        </div>

        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">
            今日新学
          </span>
          <div className="text-2xl font-extrabold text-blue-600">
            {stats?.new_today ?? 0}
          </div>
          <span className="text-[11px] text-slate-400 mt-1 block">个全新卡片</span>
        </div>

        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm col-span-2 lg:col-span-1">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">
            当前到期卡片
          </span>
          <div className="text-2xl font-extrabold text-amber-600">
            {stats?.due ?? 0}
          </div>
          <span className="text-[11px] text-slate-400 mt-1 block">等待今天复习</span>
        </div>
      </div>

      {/* Review Activity Heatmap / Bar Chart */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
            <Flame className="w-4 h-4 text-emerald-600" />
            近期复习频次趋势 (最近 21 个学习日)
          </h3>
          <span className="text-xs text-slate-400 font-mono">柱高代表每日复习卡片次数</span>
        </div>
        <ActivityChart data={stats?.review_days || []} />
      </div>

      {/* Ratings Distribution Breakdown */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
        <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-blue-600" />
          FSRS 评分分布与认知负荷
        </h3>
        <p className="text-xs text-slate-500">
          根据 FSRS 记忆模型，若「Again / Hard」占比过高，建议降低每日新词量或在设置中调低目标记忆率以避免复习雪崩。
        </p>

        {/* Multi-segment Progress Bar */}
        <div className="h-4 w-full bg-slate-100 rounded-full overflow-hidden flex shadow-inner">
          <div style={{ width: `${againPct}%` }} className="bg-red-500" title={`Again: ${againCount} (${againPct}%)`} />
          <div style={{ width: `${hardPct}%` }} className="bg-amber-400" title={`Hard: ${hardCount} (${hardPct}%)`} />
          <div style={{ width: `${normalPct}%` }} className="bg-blue-500" title={`Normal(Good): ${normalCount} (${normalPct}%)`} />
          <div style={{ width: `${easyPct}%` }} className="bg-emerald-500" title={`Easy: ${easyCount} (${easyPct}%)`} />
        </div>

        {/* Legend */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs pt-2">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-sm bg-red-500" />
            <span className="text-slate-600">重来: <strong className="text-slate-900">{againCount}</strong> ({againPct}%)</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-sm bg-amber-400" />
            <span className="text-slate-600">困难: <strong className="text-slate-900">{hardCount}</strong> ({hardPct}%)</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-sm bg-blue-500" />
            <span className="text-slate-600">良好: <strong className="text-slate-900">{normalCount}</strong> ({normalPct}%)</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-sm bg-emerald-500" />
            <span className="text-slate-600">简单: <strong className="text-slate-900">{easyCount}</strong> ({easyPct}%)</span>
          </div>
        </div>
      </div>

      {/* Recent Reviews Log Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/50 flex items-center justify-between">
          <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
            <Clock className="w-4 h-4 text-slate-500" />
            最近复习流水 (最新 50 条)
          </h3>
          <span className="text-xs text-slate-500">累计记录: {totalReviews} 条</span>
        </div>

        {reviews.length === 0 ? (
          <div className="text-center py-12 text-slate-400 text-xs">
            暂无复习记录，完成卡片评分后此处会自动同步。
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200 text-slate-500 font-semibold">
                  <th className="py-2.5 px-4">复习时间</th>
                  <th className="py-2.5 px-4">单词</th>
                  <th className="py-2.5 px-4">打分</th>
                  <th className="py-2.5 px-4">耗时</th>
                  <th className="py-2.5 px-4">排期天数</th>
                  <th className="py-2.5 px-4">稳定性 (S)</th>
                  <th className="py-2.5 px-4">难度 (D)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-mono">
                {reviews.map((r) => {
                  const dateStr = new Date(r.reviewed_at).toLocaleString('zh-CN', {
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  });
                  return (
                    <tr key={r.id} className="hover:bg-slate-50/60 font-sans">
                      <td className="py-2.5 px-4 text-slate-500 font-mono text-[11px] whitespace-nowrap">
                        {dateStr}
                      </td>
                      <td className="py-2.5 px-4 font-bold text-slate-900">
                        <div className="flex items-center gap-1.5">
                          <span className={r.undone_at ? 'line-through text-slate-400 font-normal' : ''}>
                            {r.word_term || r.word_id || r.card_id.slice(0, 8)}
                          </span>
                          {r.undone_at && (
                            <span className="px-1.5 py-0.2 bg-slate-200 text-slate-600 rounded text-[9px] font-mono">
                              已撤销
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-2.5 px-4 whitespace-nowrap">
                        {getRatingBadge(r.rating)}
                      </td>
                      <td className="py-2.5 px-4 text-slate-600 font-mono text-[11px]">
                        {r.duration_ms ? `${(r.duration_ms / 1000).toFixed(1)}s` : '-'}
                      </td>
                      <td className="py-2.5 px-4 text-slate-600 font-mono text-[11px]">
                        {r.scheduled_days !== undefined ? `${r.scheduled_days}天` : '-'}
                      </td>
                      <td className="py-2.5 px-4 text-slate-600 font-mono text-[11px]">
                        {r.stability ? r.stability.toFixed(2) : '-'}
                      </td>
                      <td className="py-2.5 px-4 text-slate-600 font-mono text-[11px]">
                        {r.difficulty ? r.difficulty.toFixed(2) : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
